import { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { AlertCircle, ImageOff, Loader2, Video } from "lucide-react";
import { useTranslation } from "react-i18next";

import { nearBottom } from "~/shared/lib/near-bottom";
import { cn } from "~/shared/lib/utils";
import { Button } from "~/shared/ui/button";
import { Checkbox } from "~/shared/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/shared/ui/dialog";
import { AudioPlayTile } from "~/shared/ui/audio-play-tile";
import { MediaLightbox } from "~/shared/ui/media-lightbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/shared/ui/tooltip";

import { PickerTabsRow } from "./PickerTabsRow";
import type { PickerImage, UseAttachmentPickerResult } from "./useAttachmentPicker";
import { usePickerDialogState } from "./usePickerDialogState";

export interface AttachmentPickerDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    picker: UseAttachmentPickerResult;
    onConfirm: () => void;
    /** i18next-prefix для всех строк диалога. */
    i18nPrefix: string;
}

export function AttachmentPickerDialog({
    open,
    onOpenChange,
    picker,
    onConfirm,
    i18nPrefix,
}: AttachmentPickerDialogProps) {
    const { t } = useTranslation("chat");
    const { selectedCount, isLoading, isError, refetch, toggle, acceptedKinds } = picker;
    const { hasMore, isLoadingMore, loadMore } = picker;
    const {
        showSpinner,
        showTabs,
        tab,
        setTab,
        filteredImages,
        lightboxImages,
        lightboxIndex,
        lightboxOpen,
        setLightboxIndex,
        handlePreview,
    } = usePickerDialogState(open, picker);

    // Фильтр вкладок клиентский, поэтому у выбранного kind медиа может не
    // хватать даже когда лента ещё не кончилась — тянем следующую страницу,
    // пока прокрутка у низа, а не только когда грид переполнен.
    const handleScroll = useCallback(
        (event: React.UIEvent<HTMLDivElement>) => {
            if (!hasMore || isLoadingMore) return;
            if (nearBottom(event.currentTarget)) loadMore();
        },
        [hasMore, isLoadingMore, loadMore],
    );

    const addLabel =
        selectedCount === 0 ? t(`${i18nPrefix}.add`) : t(`${i18nPrefix}.addN`, { count: selectedCount });

    return (
        // Пока лайтбокс открыт — DialogContent невидим (см. feedback_lightbox_inside_radix_dialog).
        <Dialog open={open} onOpenChange={onOpenChange} modal={!lightboxOpen}>
            <DialogContent
                className={cn(
                    // Фикс-высота через CSS min(): 80% viewport'а, не более 720px.
                    // Без этого DialogContent сжимался к контенту, и при
                    // переключении табов (image-grid → пустой voice) высота
                    // прыгала. max-h-[80vh] страховка для очень коротких
                    // viewport'ов (мобила в landscape).
                    "flex h-[min(80vh,720px)] max-h-[80vh] w-full max-w-3xl flex-col gap-3 sm:max-w-3xl",
                    lightboxOpen && "pointer-events-none opacity-0",
                )}
                onEscapeKeyDown={(e) => {
                    if (lightboxOpen) {
                        setLightboxIndex(null);
                        e.preventDefault();
                    }
                }}
            >
                <DialogHeader>
                    <DialogTitle>{t(`${i18nPrefix}.title`)}</DialogTitle>
                    <DialogDescription>{t(`${i18nPrefix}.description`)}</DialogDescription>
                </DialogHeader>

                {showTabs && (
                    <PickerTabsRow
                        tab={tab}
                        onTabChange={setTab}
                        acceptedKinds={acceptedKinds}
                        i18nPrefix={i18nPrefix}
                    />
                )}

                {/* Высоту теперь фиксирует DialogContent целиком — здесь
                    стандартный flex-column-scroll-pattern (flex-1 + min-h-0
                    + overflow-y-auto). Никаких min-h на body, чтобы при
                    переключении табов высота не прыгала. */}
                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" onScroll={handleScroll}>
                    {isLoading ? (
                        <LoadingPlaceholder visible={showSpinner} />
                    ) : isError ? (
                        <ErrorState onRetry={refetch} i18nPrefix={i18nPrefix} />
                    ) : filteredImages.length === 0 ? (
                        <EmptyState i18nPrefix={i18nPrefix} />
                    ) : (
                        <>
                            <ImagesGrid
                                images={filteredImages}
                                onToggle={toggle}
                                onPreview={handlePreview}
                                i18nPrefix={i18nPrefix}
                            />
                            {/* Догрузка идёт вслед за прокруткой, поэтому длина
                                чата не ограничивает выбор: за старыми медиа
                                достаточно долистать грид вниз. */}
                            {hasMore && (
                                <div
                                    className="flex items-center justify-center py-4"
                                    aria-hidden={!isLoadingMore}
                                >
                                    {isLoadingMore ? (
                                        <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
                                    ) : null}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <DialogFooter>
                    <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                        {t(`${i18nPrefix}.cancel`)}
                    </Button>
                    <Button type="button" variant="accent" onClick={onConfirm} disabled={selectedCount === 0}>
                        {addLabel}
                    </Button>
                </DialogFooter>

                {createPortal(
                    <MediaLightbox
                        items={lightboxImages}
                        initialIndex={lightboxIndex ?? 0}
                        isOpen={lightboxIndex !== null}
                        onClose={() => setLightboxIndex(null)}
                        showShare
                    />,
                    document.body,
                )}
            </DialogContent>
        </Dialog>
    );
}

// Loading-state без skeleton-плиток: пустая зона той же высоты, что и
// заполненный grid (родитель — flex-1 fix-height), плюс центральный
// Loader2-спиннер. `visible` управляется useDelayedFlag(200ms): на
// быстрых ответах пользователь не увидит ни мерцания плиток, ни
// спиннера — только мгновенно появится grid.
function LoadingPlaceholder({ visible }: { visible: boolean }) {
    return (
        <div className="flex flex-1 items-center justify-center">
            {visible ? <Loader2 className="h-6 w-6 animate-spin text-zinc-500" aria-hidden /> : null}
        </div>
    );
}

function EmptyState({ i18nPrefix }: { i18nPrefix: string }) {
    const { t } = useTranslation("chat");
    return (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center text-zinc-500">
            <ImageOff className="h-10 w-10" />
            <p className="text-sm">{t(`${i18nPrefix}.empty`)}</p>
        </div>
    );
}

function ErrorState({ onRetry, i18nPrefix }: { onRetry: () => void; i18nPrefix: string }) {
    const { t } = useTranslation("chat");
    return (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center text-rose-300">
            <AlertCircle className="h-10 w-10" />
            <p className="text-sm">{t(`${i18nPrefix}.errorLoad`)}</p>
            <Button variant="outline" size="sm" onClick={onRetry}>
                {t(`${i18nPrefix}.retry`)}
            </Button>
        </div>
    );
}

interface ImagesGridProps {
    images: PickerImage[];
    onToggle: (s3Key: string) => void;
    onPreview: (image: PickerImage) => void;
    i18nPrefix: string;
}

function ImagesGrid({ images, onToggle, onPreview, i18nPrefix }: ImagesGridProps) {
    return (
        <div className="grid grid-cols-3 gap-2 md:grid-cols-5 sm:grid-cols-4">
            {images.map((img) => (
                <ImageTile
                    key={img.id}
                    image={img}
                    onToggle={() => onToggle(img.s3Key)}
                    onPreview={() => onPreview(img)}
                    i18nPrefix={i18nPrefix}
                />
            ))}
        </div>
    );
}

interface ImageTileProps {
    image: PickerImage;
    onToggle: () => void;
    onPreview: () => void;
    i18nPrefix: string;
}

// Превью первого кадра через Media Fragment URI `#t=0.1` — браузер
// сикает на 0.1s и отрисовывает кадр без autoplay. Если metadata не
// загрузилась (медленная сеть, повреждённый файл, CORS) — `onError`
// переключает на иконку Video.
function VideoFrameOrFallback({ src }: { src: string }) {
    const [failed, setFailed] = useState(false);
    if (failed) {
        return (
            <div className="flex h-full w-full items-center justify-center bg-zinc-900 text-violet-300">
                <Video className="h-10 w-10" />
            </div>
        );
    }
    return (
        <video
            src={`${src}#t=0.1`}
            preload="metadata"
            muted
            playsInline
            className="h-full w-full object-cover"
            onError={() => setFailed(true)}
        />
    );
}

function ImageTile({ image, onToggle, onPreview, i18nPrefix }: ImageTileProps) {
    const { t } = useTranslation("chat");
    const { isSelected, alreadyAdded, selectionDisabledReason, fileName, s3Url, kind } = image;
    const checkboxDisabled = selectionDisabledReason !== null;
    const checked = alreadyAdded || isSelected;

    const tooltipText =
        selectionDisabledReason === "alreadyAdded"
            ? t(`${i18nPrefix}.alreadyAdded`)
            : selectionDisabledReason === "limitReached"
              ? t(`${i18nPrefix}.limitReached`)
              : selectionDisabledReason === "kindNotAccepted"
                ? t(`${i18nPrefix}.kindNotAccepted`)
                : null;

    const checkboxNode = (
        <span
            className="absolute top-2 right-2 z-10 flex h-6 w-6 items-center justify-center rounded-md bg-black/70 backdrop-blur-md"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
        >
            <Checkbox
                checked={checked}
                disabled={checkboxDisabled}
                onCheckedChange={() => onToggle()}
                aria-label={fileName}
            />
        </span>
    );

    return (
        <div
            className={cn(
                "group relative aspect-square overflow-hidden rounded-md border bg-zinc-900",
                checked
                    ? "border-violet-400 ring-2 ring-violet-400/60 ring-inset"
                    : "border-white/5 hover:border-white/10",
            )}
            title={fileName}
        >
            {kind === "image" ? (
                <img
                    src={s3Url}
                    alt={fileName}
                    loading="lazy"
                    className="h-full w-full object-cover"
                    draggable={false}
                />
            ) : kind === "video" ? (
                <VideoFrameOrFallback src={s3Url} />
            ) : (
                <AudioPlayTile
                    src={s3Url}
                    label={{
                        play: t(`${i18nPrefix}.audioPlay`, { name: fileName }),
                        pause: t(`${i18nPrefix}.audioPause`, { name: fileName }),
                    }}
                />
            )}

            {kind === "image" && (
                <button
                    type="button"
                    onClick={onPreview}
                    aria-label={t(`${i18nPrefix}.preview`, { name: fileName })}
                    className="absolute inset-0 cursor-zoom-in bg-transparent transition-colors hover:bg-black/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
                />
            )}

            {tooltipText ? (
                <Tooltip>
                    <TooltipTrigger asChild>{checkboxNode}</TooltipTrigger>
                    <TooltipContent>{tooltipText}</TooltipContent>
                </Tooltip>
            ) : (
                checkboxNode
            )}
        </div>
    );
}
